import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import React, { useMemo } from 'react';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import type { WorkOrderTemplate } from '#/graphql/types';
import If from '#/common/components/If';
import { toTitleCase } from '#/common/utils/formatters';
import { executionIntervals } from '#/modules/wo-template/utils/constants';
import { Impact, impacts, priorities, Priority, types } from '#/config/mappings/mappings';
import { splitCamelCase } from '#/common/utils/formatters/splitCamelCase';

export interface TemplateViewerProps {
  template: WorkOrderTemplate | undefined;
}

const fields: (keyof WorkOrderTemplate)[] = [
  'executionInterval',
  'title',
  'description',
  'type',
  'priority',
  'impact',
  'notes',
];

const TemplateViewer: React.FC<TemplateViewerProps> = ({ template }) => {
  const templ = useMemo(() => {
    return template?.id
      ? template
      : {
          __typename: 'WorkOrderTemplate',
        };
  }, [template]) as WorkOrderTemplate;

  const getDisplayValues = (field: keyof WorkOrderTemplate): string => {
    let output = '';
    const value = String(templ?.[field] ?? '-');

    switch (field) {
      case 'priority':
        const priorityLabel = priorities[Number(template?.priority) as Priority]?.label ?? '';
        output = priorityLabel;
        break;

      case 'impact':
        const impactLabel = impacts[Number(template?.impact) as Impact]?.label ?? '';
        output = impactLabel;
        break;

      case 'type':
        const typeLabel = types[template?.type as keyof typeof types]?.label ?? '';
        output = typeLabel;
        break;

      case 'executionInterval':
        const executionIntervalLabel =
          executionIntervals.find((x) => x.key === template?.executionInterval)?.label ?? '';
        output = executionIntervalLabel;
        break;

      default:
        output = value;
        break;
    }

    return output || '-';
  };

  return (
    <NuvoBox display="flex" flexDirection="column" gap={8}>
      <If condition={!!template}>
        {fields.map((field) => (
          <NuvoBox key={field}>
            <NuvoLabel>{splitCamelCase(toTitleCase(field))}</NuvoLabel>
            <NuvoText type="body" bold>
              {toTitleCase(getDisplayValues(field as keyof WorkOrderTemplate))}
            </NuvoText>
          </NuvoBox>
        ))}
      </If>
      <If condition={!template}>
        <NuvoText type="header4">Please, select a template!</NuvoText>
      </If>
    </NuvoBox>
  );
};

export default React.memo(TemplateViewer);
